import React from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { useTheme } from '../../context/ThemeContext';

export interface FormInputProps extends Omit<TextFieldProps, 'variant'> {
  variant?: 'outlined' | 'filled';
}

const FormInput: React.FC<FormInputProps> = ({
  variant = 'outlined',
  sx,
  ...props
}) => {
  const { theme } = useTheme();
  const input = theme.components.input;

  return (
    <TextField
      variant={variant}
      data-testid={props['data-testid'] ?? 'form-input'}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: input.background,
          borderRadius: '8px',
          '& fieldset': {
            borderColor: input.border,
          },
          '&:hover fieldset': {
            borderColor: input.focus,
          },
          '&.Mui-focused fieldset': {
            borderColor: input.focus,
            boxShadow: input.focusRing ?? 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: input.disabled ?? theme.gray[100],
          },
        },
        '& .MuiFilledInput-root': {
          backgroundColor: input.background,
          borderRadius: '8px 8px 0 0',
          '&:hover': {
            backgroundColor: input.background,
          },
          '&.Mui-focused': {
            backgroundColor: input.background,
          },
          '&.Mui-disabled': {
            backgroundColor: input.disabled ?? theme.gray[100],
          },
        },
        '& .MuiInputBase-input': {
          color: theme.text,
        },
        '& .MuiInputBase-input::placeholder': {
          color: input.placeholder ?? theme.gray[400],
          opacity: 1,
        },
        '& .MuiInputLabel-root': {
          color: input.placeholder ?? theme.gray[500],
          '&.Mui-focused': {
            color: input.focus,
          },
        },
        ...sx,
      }}
      {...props}
    />
  );
};

export default FormInput;
